// Sends the visitor to a random product or post from the sources Liquid lists in JSON.
// Collection sources pick a random product via the collection's products.json (one product per page);
// list sources pick from pre-rendered URLs. Optional "weight" sets how often a source is chosen. The anchor's href is the no-JS fallback.
// Repeats are kept low with a per-source "shuffle bag" saved in localStorage: every item in a source is dealt once,
// in random order, before any item comes around again. A short recent-history list also skips the page you're on
// and anything seen in the last few clicks across sources.
const STORAGE_PREFIX = 'wane-random:';
const RECENT_KEY = `${STORAGE_PREFIX}recent`;
const RECENT_SIZE = 12;
const MAX_ATTEMPTS = 4;

class RandomPage extends HTMLElement {
  #abort = null;
  #sources = [];
  #memory = new Map();

  connectedCallback() {
    const data = this.querySelector('script[type="application/json"]');
    const link = this.querySelector('a');
    if (!data || !link) return;
    try {
      this.#sources = JSON.parse(data.textContent).filter((source) =>
        source.type === 'collection' ? source.count > 0 && source.url : Array.isArray(source.urls) && source.urls.length > 0
      );
    } catch {
      return;
    }
    if (this.#sources.length === 0) return;

    this.#abort = new AbortController();
    link.addEventListener('click', (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.#go(link);
    }, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  // localStorage can be blocked (private mode, storage full); fall back to memory for this page view.
  #read(key) {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return this.#memory.get(key) ?? null;
    }
  }

  #write(key, value) {
    this.#memory.set(key, value);
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Memory copy above is enough for this visit.
    }
  }

  // Weighted pick: each source counts as its "weight" (default 1), so a low weight makes it rare.
  #pick() {
    const weightOf = (source) => (typeof source.weight === 'number' && source.weight >= 0 ? source.weight : 1);
    const total = this.#sources.reduce((sum, source) => sum + weightOf(source), 0);
    let roll = Math.random() * total;
    for (const source of this.#sources) {
      roll -= weightOf(source);
      if (roll < 0) return source;
    }
    return this.#sources[0];
  }

  // Deals the next index (0-based) from a source's shuffle bag, refilling it once every item has been dealt.
  #deal(source) {
    const size = source.type === 'collection' ? source.count : source.urls.length;
    const key = `${STORAGE_PREFIX}bag:${source.url || source.urls[0]}:${size}`;
    let state = this.#read(key);
    if (!state || !Array.isArray(state.bag) || state.bag.some((i) => typeof i !== 'number' || i >= size)) {
      state = { bag: [], last: -1 };
    }
    if (state.bag.length === 0) {
      const bag = Array.from({ length: size }, (_, i) => i);
      for (let i = bag.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [bag[i], bag[j]] = [bag[j], bag[i]];
      }
      // Never open a fresh round with the item that closed the last one.
      if (bag.length > 1 && bag[bag.length - 1] === state.last) {
        [bag[0], bag[bag.length - 1]] = [bag[bag.length - 1], bag[0]];
      }
      state.bag = bag;
    }
    const index = state.bag.pop();
    state.last = index;
    this.#write(key, state);
    return index;
  }

  async #resolve(source) {
    const index = this.#deal(source);
    if (source.type !== 'collection') return source.urls[index];
    // products.json is Shopify's storefront JSON endpoint; limit=1 makes page index a product index.
    const response = await fetch(`${source.url}/products.json?limit=1&page=${index + 1}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return source.url;
    const json = await response.json();
    const handle = json?.products?.[0]?.handle;
    return handle ? `${source.url}/products/${handle}` : source.url;
  }

  async #go(link) {
    link.setAttribute('aria-busy', 'true');
    const recent = this.#read(RECENT_KEY);
    const recentList = Array.isArray(recent) ? recent : [];
    const here = window.location.pathname;
    // Compare by the trailing path (handle) so /collections/x/products/y and /products/y count as the same piece.
    const tail = (url) => {
      try {
        return new URL(url, window.location.href).pathname.split('/').filter(Boolean).slice(-2).join('/');
      } catch {
        return url;
      }
    };
    const seen = new Set([tail(here), ...recentList]);

    let destination = link.href;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      const source = this.#pick();
      try {
        destination = await this.#resolve(source);
      } catch {
        destination = source.url || link.href;
      }
      if (!seen.has(tail(destination))) break;
    }

    this.#write(RECENT_KEY, [tail(destination), ...recentList.filter((item) => item !== tail(destination))].slice(0, RECENT_SIZE));
    window.location.assign(destination);
  }
}

if (!customElements.get('s-random-page')) customElements.define('s-random-page', RandomPage);
